import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { IBaseProps } from '@rbi-ctg/frontend';
import { useFeatureEarningCalculationQuery } from 'generated/sanity-graphql';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useEffectOnce from 'hooks/use-effect-once';
import { usePrevious } from 'hooks/use-previous';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { IUseLoyaltyOffers, IUseLoyaltyRewards, IUseLoyaltyUserState } from './hooks/types';
import { useGetOffersIncentivesIds } from './hooks/use-get-offers-incentives';
import { useIsLoyaltyEnabled } from './hooks/use-is-loyalty-enabled';
import { useLoyaltyOffers } from './hooks/use-loyalty-offers';
import { useLoyaltyRewards } from './hooks/use-loyalty-rewards';
import { useLoyaltyUserState } from './hooks/use-loyalty-user-state';
import { InRestaurantRedemptionProvider } from './in-restaurant-redemption';

// Leaving as an interface to be extended in future PR
export interface ILoyaltyContext
  extends IUseLoyaltyRewards,
    IUseLoyaltyOffers,
    IUseLoyaltyUserState {
  claimPointsEnabled: boolean;
  refetchLoyaltyUser: () => void;
}

export const LoyaltyContext = createContext({} as ILoyaltyContext);
export const useLoyaltyContext = () => useContext(LoyaltyContext);

export const LoyaltyProvider = (props: IBaseProps) => {
  const claimPointsEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_CLAIM_LOYALTY_POINTS));
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const loyaltyUserState = useLoyaltyUserState();
  const {
    loading: loadingLoyaltyUser,
    loyaltyUser,
    refetch: refetchLoyaltyUser,
  } = loyaltyUserState;

  const { data: earningCalculationData, loading: earningCalculationDataLoading } =
    useFeatureEarningCalculationQuery({
      variables: { id: 'earningCalculation' },
    });

  const loyaltyUserId = loyaltyUser?.id;
  const prevLoyaltyUserId = usePrevious(loyaltyUserId);
  const loyaltyUserPoints = loyaltyUser?.points ?? 0;

  const loyaltyRewards = useLoyaltyRewards(loyaltyUser);
  const loyaltyOffers = useLoyaltyOffers();
  const { fetchIncentiveIds } = useGetOffersIncentivesIds();
  const dispatch = useAppDispatch();
  const shouldRefetch = useAppSelector(selectors.loyalty.selectShouldRefetchRewards);

  useEffect(() => {
    dispatch(
      actions.loyalty.setMaxDailyRewardRedemptionLimit(
        earningCalculationData?.EarningCalculation?.transactionEarningConfiguration
          ?.maxRewardRedemptionLimit || Infinity
      )
    );
  }, [earningCalculationDataLoading]);

  useEffectOnce(() => {
    if (loyaltyEnabled) {
      fetchIncentiveIds().then(ids => dispatch(actions.loyalty.setIncentivesIds(ids || [])));
    }
  });

  useEffectOnUpdates(() => {
    if (!loyaltyEnabled) {
      dispatch(actions.loyalty.resetLoyaltyRewardsState({ points: loyaltyUserPoints }));
    }
  }, [dispatch, loyaltyEnabled, loyaltyUserPoints]);

  const { refetchRewards, engineRewardsMap } = loyaltyRewards;
  // Reset rewards on loyalty user change
  useEffect(() => {
    if (prevLoyaltyUserId && !loadingLoyaltyUser && prevLoyaltyUserId !== loyaltyUserId) {
      dispatch(actions.loyalty.resetLoyaltyRewardsState({ points: loyaltyUserPoints }));
    }
    if (loyaltyUserId) {
      dispatch(actions.loyalty.setShouldRefetchRewards(true));
    }
  }, [dispatch, loadingLoyaltyUser, loyaltyUserId, loyaltyUserPoints, prevLoyaltyUserId]);

  useEffect(() => {
    if (shouldRefetch && loyaltyUserId) {
      refetchRewards(loyaltyUserId);
      dispatch(actions.loyalty.setShouldRefetchRewards(false));
    }
  }, [dispatch, loyaltyUserId, refetchRewards, shouldRefetch]);

  useEffect(() => {
    const engineRewardsList = Object.values(engineRewardsMap);
    if (engineRewardsList?.length) {
      dispatch(actions.loyalty.createAvailableLoyaltyRewardsMap(engineRewardsList));
    }
  }, [dispatch, engineRewardsMap]);

  const loyaltyContextValue = useMemo(
    () => ({
      claimPointsEnabled,
      refetchLoyaltyUser,
      // other loyalty hooks
      ...loyaltyUserState,
      ...loyaltyOffers,
      ...loyaltyRewards,
    }),
    [loyaltyUserState, loyaltyOffers, loyaltyRewards, claimPointsEnabled, refetchLoyaltyUser]
  );

  return (
    <LoyaltyContext.Provider value={loyaltyContextValue}>
      <InRestaurantRedemptionProvider>{props.children}</InRestaurantRedemptionProvider>
    </LoyaltyContext.Provider>
  );
};
